import React from 'react';
import styled from 'styled-components';
import { SubmitHandler, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mantine/core';

import { planApis } from '../../../../shared/apis/plan/apis';
import type { IEditPlanPayload, IPlanUpsertPayload } from '../../../../shared/apis/plan/types';
import { queries } from '../../../../shared/queries';
import type { IPlanFormValues } from '../../../../shared/types/plan';
import * as C from '../../../ui/common/CommonStyles';
import useGetPlanDetails from '../../../../shared/hook/queries/useGetPlanDetails';

interface IPropsPlanRegModal {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEdit: boolean;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  editItemId?: number | null;
  setEditItemId?: React.Dispatch<React.SetStateAction<number | null>>;
  refetch: () => void;
}

const initValue: IPlanFormValues = {
  planName: '',
  planDisplayName: '',
  planType: 1,
  planPeriod: 12,
  regularPrice: '',
  discountPrice: '',
  memo: '',
};

const PlanRegModal = (props: IPropsPlanRegModal) => {
  const { isOpen, setIsOpen, isEdit, setIsEdit, editItemId, setEditItemId, refetch } = props;

  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    // watch,
    // setValue,
    reset,
    formState: { errors },
  } = useForm<IPlanFormValues>({
    mode: 'onChange',
    defaultValues: initValue,
  });
  const { data: editItem } = useGetPlanDetails({
    id: editItemId!,
    onSuccess: (data) => {
      reset({
        planName: data?.planName || '',
        planDisplayName: data?.planDisplayName || '',
        planPeriod: data?.planPeriod || 12,
        planType: data?.planType || 0,
        regularPrice: data?.regularPrice?.toString() || '',
        discountPrice: data?.discountPrice?.toString() || '',
        memo: data?.memo || '',
      });
    },
  });

  // const selectedPayType = watch('planType');

  // const onPayTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const selectedPayType = e.target.value;
  //   setValue('planType', selectedPayType);
  //   if (selectedPayType === 'subPlan') {
  //     setValue('planPeriod', '1');
  //   } else if (selectedPayType === 'singlePlan') {
  //     setValue('planPeriod', '12');
  //   }
  // };

  const closeModal = () => {
    Swal.fire({
      icon: 'warning',
      title: '작성을 취소하시겠습니까?',
      text: '작성 중인 내용은 저장되지 않습니다.',
      showCancelButton: true,
      cancelButtonText: '취소',
      confirmButtonText: '확인',
    }).then((result) => {
      if (result.isConfirmed) {
        reset(initValue);
        setIsEdit(false);
        setIsOpen(false);
      }
    });
  };

  const { mutate: postPlan } = useMutation((data: IPlanUpsertPayload) => planApis.postDevicePlan(data), {
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: '성공적으로 등록되었습니다.',
      }).then(() => {
        queryClient.invalidateQueries(queries.plans.list._def);
        reset(initValue);
        setIsOpen(false);
        refetch();
      });
    },
  });

  const { mutate: editPlan } = useMutation((editData: IEditPlanPayload) => planApis.editPlan(editData), {
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: '성공적으로 수정되었습니다.',
      }).then(() => {
        if (setEditItemId) {
          queryClient.invalidateQueries(queries.plans.list._def);
          reset(initValue);
          setEditItemId(null);
          setIsOpen(false);
          refetch();
        }
      });
    },
  });

  // 정상가와 판매가 비교
  const validatePrice = (regularPrice: string, discountPrice: string) => {
    const regularPriceNum = Number(regularPrice.replace(/,/g, ''));
    const discountPriceNum = Number(discountPrice.replace(/,/g, ''));

    if (regularPriceNum < discountPriceNum) {
      return false;
    }
    return true;
  };

  // 요금제 생성 페이로드 생성
  const createPostData = (data: IPlanFormValues): IPlanUpsertPayload => {
    return {
      planName: data.planName,
      planDisplayName: data.planDisplayName,
      // payType: data.payType === 'subPlan' ? '정기결제' : '단건결제',
      // payInterval: parseInt(data.payInterval),
      planType: 0, // 현재는 단건 결제만 존재하여 0으로 고정
      planPeriod: 12, // 현재는 1년으로 고정
      regularPrice: parseInt(data.regularPrice),
      discountPrice: parseInt(data.discountPrice),
      memo: data.memo,
    };
  };

  // 요금제 수정 페이로드 생성
  const createEditData = (data: IPlanFormValues): IEditPlanPayload | null => {
    if (editItem && editItem.id) {
      return {
        id: editItem?.id,
        data: { memo: data.memo },
      };
    }
    return null;
  };

  const onSubmit: SubmitHandler<IPlanFormValues> = (data) => {
    const isValid = validatePrice(data.regularPrice, data.discountPrice);
    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: '판매가는 정상가를 초과할 수 없습니다.',
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: `요금제를 ${isEdit ? '수정' : '등록'}하시겠습니까?`,
      showCancelButton: true,
      confirmButtonText: isEdit ? '수정' : '등록',
      cancelButtonText: '취소',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isEdit) {
          const sendData = createEditData(data);
          sendData && editPlan(sendData);
          return;
        }
        const sendData = createPostData(data);
        postPlan(sendData);
      }
    });
  };

  return (
    <React.Fragment>
      {isOpen ? (
        <C.ModalWrap>
          <C.Modal>
            <ModalContent>
              <Title>신규 요금제 생성</Title>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label className="label" htmlFor="planName">
                    <RequiredField>*</RequiredField>
                    요금제명
                  </label>
                  <input
                    id="planName"
                    placeholder="관리를 위한 요금제명입니다."
                    {...register('planName', {
                      required: '요금제명을 입력해주세요.',
                      maxLength: {
                        value: 20,
                        message: '최대 20자까지 입력 가능합니다.',
                      },
                    })}
                    disabled={isEdit}
                  />
                  {errors.planName && <Error>{errors.planName.message?.toString()}</Error>}
                </div>
                <div>
                  <label className="label" htmlFor="planDisplayName">
                    <RequiredField>*</RequiredField>
                    화면 표시용 요금제명
                  </label>
                  <input
                    id="planDisplayName"
                    placeholder="사용자 화면에 표시되는 요금제명입니다."
                    {...register('planDisplayName', {
                      required: '화면 표시용 요금제명을 입력해주세요.',
                      maxLength: {
                        value: 20,
                        message: '최대 20자까지 입력 가능합니다.',
                      },
                    })}
                    disabled={isEdit}
                  />
                  {errors.planDisplayName && <Error>{errors.planDisplayName.message?.toString()}</Error>}
                </div>
                {/* <div>
                  <div className="label">
                    <RequiredField>*</RequiredField>
                    결제 타입
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="subPlan"
                      value="subPlan"
                      {...register('planType')}
                      checked={selectedPayType === 'subPlan'}
                      onChange={onPayTypeChange}
                      disabled={isEdit}
                    />
                    <RadioLabel htmlFor="subPlan">구독 결제</RadioLabel>
                    <input
                      type="radio"
                      id="singlePlan"
                      value="singlePlan"
                      {...register('planType')}
                      checked={selectedPayType === 'singlePlan'}
                      onChange={onPayTypeChange}
                      disabled={isEdit}
                    />
                    <RadioLabel htmlFor="singlePlan">단건 결제</RadioLabel>
                  </div>
                </div>
                <div>
                  <div className="label">
                    <RequiredField>*</RequiredField>
                    결제 주기
                  </div>
                  <div>
                    {selectedPayType === 'subPlan' && (
                      <React.Fragment>
                        <input
                          type="radio"
                          id="m1"
                          value="1"
                          {...register('planPeriod')}
                          onChange={() => setValue('planPeriod', '1')}
                          disabled={isEdit}
                        />
                        <RadioLabel htmlFor="m1">1개월</RadioLabel>
                      </React.Fragment>
                    )}
                    {selectedPayType === 'singlePlan' && (
                      <React.Fragment>
                        <input
                          type="radio"
                          id="m12"
                          value="12"
                          {...register('planPeriod')}
                          onChange={() => setValue('planPeriod', '12')}
                          disabled={isEdit}
                        />
                        <RadioLabel htmlFor="m12">1년</RadioLabel>
                      </React.Fragment>
                    )}
                  </div>
                </div> */}
                <div>
                  <label className="label" htmlFor="regularPrice">
                    <RequiredField>*</RequiredField>
                    정상가
                  </label>
                  <input
                    type="text"
                    id="regularPrice"
                    placeholder="할인 전 금액입니다."
                    {...register('regularPrice', {
                      required: '정상가를 입력해주세요.',
                      pattern: {
                        value: /^[0-9,]*$/,
                        message: '숫자만 입력 가능합니다.',
                      },
                    })}
                    disabled={isEdit}
                  />
                  {errors.regularPrice && <Error>{errors.regularPrice.message?.toString()}</Error>}
                </div>
                <div>
                  <label className="label" htmlFor="discountPrice">
                    <RequiredField>*</RequiredField>
                    판매가(최종 표시금액)
                  </label>
                  <input
                    type="text"
                    id="discountPrice"
                    placeholder="고객이 결제하는 금액입니다."
                    {...register('discountPrice', {
                      required: '판매가를 입력해주세요.',
                      pattern: {
                        value: /^[0-9,]*$/,
                        message: '숫자만 입력 가능합니다.',
                      },
                    })}
                    disabled={isEdit}
                  />
                  {errors.discountPrice && <Error>{errors.discountPrice.message?.toString()}</Error>}
                </div>
                <div>
                  <label className="label" htmlFor="memo">
                    메모
                  </label>
                  <textarea
                    id="memo"
                    placeholder="요금제 관련 특이사항 등 입력"
                    {...register('memo', {
                      maxLength: {
                        value: 200,
                        message: '최대 200자까지 입력 가능합니다.',
                      },
                    })}
                  />
                  {errors.memo && <Error>{errors.memo.message?.toString()}</Error>}
                </div>
                <ButtonBox>
                  <Button type="button" variant="outline" size="md" onClick={closeModal}>
                    취소
                  </Button>
                  <Button type="submit" variant="filled" size="md" color="secondary.5">
                    {isEdit ? '수정' : '등록'}
                  </Button>
                </ButtonBox>
              </Form>
            </ModalContent>
          </C.Modal>
        </C.ModalWrap>
      ) : null}
    </React.Fragment>
  );
};

export default PlanRegModal;

const ModalContent = styled(C.ModalContent)`
  padding: 16px 62px;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 10px;
  width: 100%;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;

  > div:not(:last-child) {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
  }

  input,
  textarea {
    padding: 8px;
    border: ${(props) => props.theme.border.gray1};
  }

  textarea {
    resize: none;
    height: 100px;
  }

  .label {
    margin-bottom: 8px;
    font-weight: 600;
  }
`;

const RequiredField = styled.span`
  color: ${(props) => props.theme.color.red};
  margin-right: 4px;
`;

const ButtonBox = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 20px;
`;

const Error = styled.p`
  color: ${(props) => props.theme.color.red};
  font-size: 12px;
  margin-top: 4px;
`;
